import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/store";
import BrandMark from "@/components/BrandMark";
import { Sparkles, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
    const { login } = useApp();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    // The demo accounts only exist where seed data was loaded; hide the hint
    // on a clean production install with REACT_APP_HIDE_DEMO_LOGIN=1.
    const showDemo = process.env.REACT_APP_HIDE_DEMO_LOGIN !== "1";
    const fillDemo = () => {
        setEmail("owner@brightsmile.demo");
        setPassword("Demo!2026");
    };
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    const googleSignIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
            redirectUrl,
        )}`;
    };

    return (
        <div className="min-h-screen grain flex">
            <div className="hidden lg:flex flex-1 relative overflow-hidden bg-gradient-to-br from-[#0B0F19] via-[#0F1226] to-[#111827]">
                <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_20%_20%,rgba(99,102,241,0.35),transparent_40%),radial-gradient(circle_at_80%_80%,rgba(56,189,248,0.25),transparent_45%)]" />
                <div className="relative flex flex-col justify-between p-14 z-10">
                    <Link to="/">
                        <BrandMark size="md" />
                    </Link>
                    <div className="max-w-lg">
                        <div className="label-eyebrow text-indigo-300 mb-3">
                            For local businesses
                        </div>
                        <h1 className="text-4xl xl:text-5xl font-extrabold text-slate-50 leading-[1.05] tracking-tight">
                            An AI Marketing Manager that <span className="text-indigo-400">detects</span>, <span className="text-cyan-400">analyses</span>,{" "}
                            <span className="text-emerald-400">recommends</span>, and{" "}
                            <span className="text-amber-400">executes</span> — with guardrails.
                        </h1>
                        <p className="mt-6 text-slate-400 text-base leading-relaxed">
                            Every review, every complaint, every KPI — grounded in
                            your own data. Sensitive replies always route through your
                            approval inbox.
                        </p>
                        <div className="mt-8 flex items-center gap-6 text-xs text-slate-500 font-mono uppercase tracking-[0.18em]">
                            <span className="flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                Autonomy
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                                Approvals
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
                                Safety override
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md surface rounded-2xl p-8 fade-in">
                    <Link to="/" className="mb-6 lg:hidden">
                        <BrandMark size="sm" />
                    </Link>
                    <h2 className="text-2xl font-bold text-slate-100 tracking-tight">
                        Sign in
                    </h2>
                    <p className="mt-1 text-sm text-slate-400">
                        Continue to your business dashboard.
                    </p>

                    <button
                        onClick={googleSignIn}
                        data-testid="google-signin-button"
                        className="mt-6 w-full flex items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] px-4 py-3 text-sm font-semibold text-slate-100 transition-colors"
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                            <path
                                fill="#EA4335"
                                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                            />
                            <path
                                fill="#4285F4"
                                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                            />
                            <path
                                fill="#FBBC05"
                                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                            />
                            <path
                                fill="#34A853"
                                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                            />
                        </svg>
                        Continue with Google
                    </button>

                    <div className="my-6 flex items-center gap-3 text-[10px] text-slate-500 font-mono uppercase tracking-[0.22em]">
                        <div className="h-px flex-1 bg-white/5" />
                        or email
                        <div className="h-px flex-1 bg-white/5" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-xs text-slate-400">
                                Email
                            </Label>
                            <Input
                                id="email"
                                data-testid="login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1.5 bg-white/[0.03] border-white/10 focus:border-indigo-500/50 focus-visible:ring-indigo-500/20 text-slate-100"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs text-slate-400">
                                Password
                            </Label>
                            <Input
                                id="password"
                                data-testid="login-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1.5 bg-white/[0.03] border-white/10 focus:border-indigo-500/50 focus-visible:ring-indigo-500/20 text-slate-100"
                                required
                            />
                        </div>
                        <Button
                            data-testid="login-submit-button"
                            type="submit"
                            disabled={busy}
                            className="w-full bg-indigo-500 hover:bg-indigo-400 text-white font-semibold rounded-xl h-11 gap-2"
                        >
                            {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Sparkles className="h-4 w-4" />
                            )}
                            Sign in
                        </Button>
                    </form>

                    {showDemo && (
                        <button
                            type="button"
                            onClick={fillDemo}
                            data-testid="login-use-demo"
                            className="mt-6 w-full text-left rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 p-3 text-[11px] text-slate-500 leading-relaxed transition-colors"
                        >
                            <div className="flex items-center gap-2 text-slate-400 font-semibold mb-1">
                                <ShieldCheck className="h-3.5 w-3.5" /> Try the demo account
                            </div>
                            <div className="mono">
                                owner@brightsmile.demo · owner@bistro22.demo
                                <br />
                                password:{" "}
                                <span className="text-slate-300">Demo!2026</span>
                                <span className="text-indigo-300"> — click to fill</span>
                            </div>
                        </button>
                    )}

                    <div className="mt-6 text-center text-xs text-slate-400">
                        Don't have an account?{" "}
                        <Link
                            to="/register"
                            data-testid="login-create-account-link"
                            className="font-semibold text-indigo-300 hover:text-indigo-200"
                        >
                            Create account
                        </Link>
                    </div>
                </div>
            </div>
            <Toaster richColors closeButton position="top-right" />
        </div>
    );
}
